import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, User, Search } from "lucide-react";
import Wordmark from "./Wordmark";
import ConsultationModal from "./ConsultationModal";
import SignInModal from "./SignInModal";
import MegaMenu from "./MegaMenu";
import MobileNav from "./MobileNav";
import HeaderSearch from "./HeaderSearch";
import PublicCmdKPalette from "./PublicCmdKPalette";
import PlaybookInboxBell from "./PlaybookInboxBell";
import EngagementsBell from "./EngagementsBell";
import { useUserAuth } from "../lib/userAuth";

export default function Header({ onConsultClick }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const [showConsult, setShow] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { account } = useUserAuth();
  const open = () => (onConsultClick ? onConsultClick() : setShow(true));

  // ⌘K / Ctrl+K opens the palette. "/" too unless the user is typing
  // somewhere else. We rebind globally so the palette is reachable
  // from any public page (it lives in the sticky Header).
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" ||
                     e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (e.key === "/" && !typing && !paletteOpen) {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [paletteOpen]);

  const isMac = typeof navigator !== "undefined" &&
                /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");

  const source = `header-${loc.pathname === "/" ? "landing" : loc.pathname.replace(/^\/+/, "").split("/")[0]}`;

  return (
    <>
      <header
        data-testid="site-header"
        role="banner"
        className="sticky top-0 z-50 qt-no-print"
        style={{ background: "#04091A", borderBottom: "1px solid var(--qt-cyan)", boxShadow: "0 1px 12px rgba(0, 229, 255, 0.18)" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 h-16 flex items-center gap-4">
          {/* Left cluster: logo + nav. Shrinks if needed to keep the
              center search bar prominent. */}
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/" data-testid="header-logo-link" aria-label="QorTrace home" className="flex items-center gap-3">
              <Wordmark size={20} />
            </Link>
            <div className="hidden lg:flex items-center">
              <MegaMenu />
            </div>
          </div>

          {/* Center: always-visible inline search bar (Stripe pattern).
              Type-ahead dropdown renders directly underneath.  ⌘K still
              opens the full-screen modal palette for power users. */}
          <div className="hidden md:flex flex-1 justify-center min-w-0 max-w-[480px] mx-auto">
            <HeaderSearch />
          </div>

          {/* Right cluster: CTAs. */}
          <div className="flex items-center gap-1 shrink-0 ml-auto">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              data-testid="header-cmdk-trigger"
              title={`Open full palette (${isMac ? "⌘K" : "Ctrl+K"})`}
              aria-label="Open search palette"
              className="qt-btn qt-btn-ghost md:hidden"
              style={{
                fontSize: 12, padding: "0.5rem 0.75rem",
                border: "1px solid var(--qt-border)",
                color: "var(--qt-text-3)",
              }}
            >
              <Search size={13} aria-hidden="true" />
            </button>
            <button
              onClick={open}
              data-testid="header-consult-cta"
              aria-label="Open consultation modal"
              className="qt-btn qt-btn-secondary"
              style={{ fontSize: 13, padding: "0.5rem 0.875rem" }}
            >
              <Calendar size={14} aria-hidden="true" /> <span className="hidden sm:inline">{t("nav.consult", "Consult")}</span>
            </button>
            {account ? (
              <>
                {/* Iter273.3 — Beta-tester P1 #7: notification bells in the
                    profile cluster. Previously the bells only mounted inside
                    AccountLayout's sidebar footer — invisible while the user
                    was browsing /methodology, /pricing, etc. They now sit
                    next to the avatar so unread engagement messages + share
                    notifications surface anywhere on the site. Hidden on
                    very small viewports to keep the right cluster compact;
                    MobileNav drawer is the mobile entry point. */}
                <div className="hidden sm:flex items-center"
                     data-testid="header-bell-cluster">
                  <PlaybookInboxBell />
                  <EngagementsBell kind="client" />
                </div>
                <Link
                  to="/account"
                  data-testid="header-account-link"
                  className="qt-btn qt-btn-ghost"
                  title={`Signed in as ${account.email} · ${(account.plan || 'free').toUpperCase()}`}
                  aria-label={`Account menu for ${account.email}, plan ${(account.plan || 'free').toUpperCase()}`}
                  style={{ fontSize: 13, padding: "0.4rem 0.55rem 0.4rem 0.4rem",
                           border: "1px solid var(--qt-border)",
                           display: "inline-flex", alignItems: "center", gap: 8 }}
                >
                  {/* Iter273.2 — Beta-tester fix: the chip used to show
                      just "FREE" with no signed-in cue, so testers
                      couldn't tell whether they were logged in and
                      mistook "FREE" for a "Free Scan" CTA. Initials
                      circle now makes the auth state unmistakable. */}
                  <span
                    aria-hidden="true"
                    style={{
                      width: 22, height: 22, borderRadius: 999,
                      background: "var(--qt-cyan, #67E8F9)",
                      color: "#04091A", fontWeight: 700,
                      fontSize: 11, lineHeight: "22px",
                      textAlign: "center", letterSpacing: 0,
                      flexShrink: 0,
                    }}
                  >
                    {(account.email || "?").trim()[0].toUpperCase()}
                  </span>
                  <span className="qt-mono text-xs hidden sm:inline"
                        style={{ color: "var(--qt-cyan)",
                                 letterSpacing: "0.1em" }}>
                    {(account.plan || "free").toUpperCase()}
                  </span>
                </Link>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setShowSignIn(true)}
                data-testid="header-signin-link"
                className="qt-btn qt-btn-ghost"
                style={{ fontSize: 13, padding: "0.5rem 0.75rem", border: "1px solid var(--qt-border)" }}
              >
                <User size={14} /> <span className="hidden sm:inline">{t("nav.signin", "Sign in")}</span>
              </button>
            )}
            <MobileNav />
          </div>
        </div>
      </header>
      {!onConsultClick && <ConsultationModal open={showConsult} onClose={() => setShow(false)} source={source} />}
      <SignInModal open={showSignIn} onClose={() => setShowSignIn(false)} defaultMode="login" />
      <PublicCmdKPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}
